import React from 'react';
import { Mail, Globe, AlertCircle, CheckCircle, XCircle, Clock } from 'lucide-react';

const RefundPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black py-20 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <header className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-yellow-400/10 rounded-lg">
              <Mail className="w-8 h-8 text-yellow-400" />
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-white">
              Refund Policy
            </h1>
          </div>
          <p className="text-sm text-gray-400">
            Last updated: November 2024
          </p>
          <p className="text-lg text-gray-300 mt-4">
            At <strong className="text-white">Fokat Main</strong>, operated by{' '}
            <strong className="text-white">PLAYEDGE INNOVATIONS</strong>, we aim to provide a fair and transparent 
            experience for all participants. This Refund Policy explains when refunds are applicable and how they are processed.
          </p>
        </header>

        {/* Content Card */}
        <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-gray-800 space-y-8">
          
          {/* Section 1: General Policy */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-yellow-400">1.</span> General Policy
            </h2>
            <div className="bg-gray-800/50 rounded-lg p-5 border border-gray-700">
              <p className="text-gray-300 leading-relaxed">
                All quizzes and giveaways hosted on Fokat Main are <strong className="text-white">skill-based</strong>, 
                and participation is considered <strong className="text-white">final</strong> once submitted. 
                Since entries are digital and based on skill participation, <strong className="text-white">refunds are generally not applicable</strong> after 
                joining a contest or quiz.
              </p>
            </div>
          </section>

          {/* Section 2: Eligibility for Refunds */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-yellow-400">2.</span> Eligibility for Refunds
            </h2>
            <p className="text-gray-300 mb-4">
              Refunds will only be considered under the following circumstances:
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3 bg-green-900/20 border border-green-800/50 rounded-lg p-4">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white font-semibold">Duplicate Payment</p>
                  <p className="text-gray-400 text-sm">For the same quiz or contest.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-green-900/20 border border-green-800/50 rounded-lg p-4">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white font-semibold">Technical Error</p>
                  <p className="text-gray-400 text-sm">Resulting in a failed entry after successful payment.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-green-900/20 border border-green-800/50 rounded-lg p-4">
                <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-white font-semibold">System Malfunction</p>
                  <p className="text-gray-400 text-sm">Directly caused by our platform.</p>
                </div>
              </div>
            </div>
            <div className="mt-4 bg-blue-900/20 border border-blue-800/50 rounded-lg p-4">
              <p className="text-gray-300">
                If you experience any of the above, you can request a refund within{' '}
                <strong className="text-white">3 business days</strong> of the transaction by emailing{' '}
                <a href="mailto:support@fokatmain.com" className="text-yellow-400 hover:underline">
                  support@fokatmain.com
                </a>{' '}
                with your transaction details and payment proof.
              </p>
            </div>
          </section>

          {/* Section 3: Non-Refundable Situations */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-yellow-400">3.</span> Non-Refundable Situations
            </h2>
            <p className="text-gray-300 mb-4">
              Refunds will <strong className="text-white">not</strong> be issued if:
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3 bg-red-900/20 border border-red-800/50 rounded-lg p-4">
                <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300">
                  The participant fails to join or complete a quiz due to network, device, or browser issues.
                </p>
              </div>
              <div className="flex items-start gap-3 bg-red-900/20 border border-red-800/50 rounded-lg p-4">
                <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300">
                  The participant is disqualified for rule violations or multiple account usage.
                </p>
              </div>
              <div className="flex items-start gap-3 bg-red-900/20 border border-red-800/50 rounded-lg p-4">
                <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300">
                  The quiz or giveaway has been completed and results have been declared.
                </p>
              </div>
              <div className="flex items-start gap-3 bg-red-900/20 border border-red-800/50 rounded-lg p-4">
                <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300">
                  The prize or giveaway item has already been dispatched.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4: Refund Method and Timeline */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-yellow-400">4.</span> Refund Method and Timeline
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3 bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <Clock className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300">
                  Approved refunds will be credited to the <strong className="text-white">original payment method</strong> used.
                </p>
              </div>
              <div className="flex items-start gap-3 bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <Clock className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300">
                  Refunds are processed within <strong className="text-white">7–10 working days</strong> after verification.
                </p>
              </div>
              <div className="flex items-start gap-3 bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <p className="text-gray-300">
                  Any payment gateway fees or third-party charges are <strong className="text-white">non-refundable</strong>.
                </p>
              </div>
            </div>
          </section>

          {/* Section 5: Cancellation Policy */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-yellow-400">5.</span> Cancellation Policy
            </h2>
            <div className="space-y-3">
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <p className="text-gray-300">
                  Once a quiz entry is submitted, it <strong className="text-white">cannot be cancelled</strong> or withdrawn.
                </p>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
                <p className="text-gray-300">
                  In exceptional cases (such as system downtime or duplicate deduction), you may contact our support team for resolution.
                </p>
              </div>
            </div>
          </section>

          {/* Section 6: Contact for Refund Queries */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-yellow-400">6.</span> Contact for Refund Queries
            </h2>
            <div className="bg-gradient-to-r from-yellow-900/20 to-orange-900/20 border border-yellow-800/50 rounded-lg p-6">
              <p className="text-gray-300 mb-4">
                If you have any concerns regarding payments or refunds, please reach out to us at:
              </p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-yellow-400" />
                  <a href="mailto:support@fokatmain.com" className="text-yellow-400 hover:underline font-semibold">
                    support@fokatmain.com
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-yellow-400" />
                  <a href="/contact" className="text-yellow-400 hover:underline font-semibold">
                    https://www.fokatmain.com/contact
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Section 7: Policy Updates */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
              <span className="text-yellow-400">7.</span> Policy Updates
            </h2>
            <div className="bg-gray-800/50 rounded-lg p-5 border border-gray-700">
              <p className="text-gray-300">
                Fokat Main reserves the right to update or modify this Refund Policy as needed. 
                All updates will be reflected on this page with a revised "Last Updated" date.
              </p>
            </div>
          </section>

          {/* Quick Links */}
          <div className="pt-6 border-t border-gray-800">
            <p className="text-sm text-gray-400 mb-3">Related Pages:</p>
            <div className="flex flex-wrap gap-3">
              <a 
                href="/faq" 
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-white transition-colors duration-200"
              >
                FAQ
              </a>
              <a 
                href="/terms" 
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-white transition-colors duration-200"
              >
                Terms & Conditions
              </a>
              <a 
                href="/privacy" 
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-white transition-colors duration-200"
              >
                Privacy Policy
              </a>
              <a 
                href="/contact" 
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-sm text-white transition-colors duration-200"
              >
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;
